/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `TermsAndConditions` for the Caintra Connect frontend.
 * @module pages/TermsAndConditions
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import styles from "../styles/TermsAndConditions.module.css";
import LanguageSelector from "../components/LanguageSelector/index.jsx";

import { ReactComponent as CaintraLogo } from "../assets/Caintra_Logo.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/CompanyEditPage/ChevronLeftIcon.svg";

const TermsAndConditions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/home");
    } else {
      navigate("/");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.returnButtonContainer} onClick={handleBack}>
          <ChevronLeftIcon className={styles.returnButtonIcon} />
          <span className={styles.returnButtonText}>{t("common.goBack")}</span>
        </button>
        <LanguageSelector />
      </div>
      <div className={styles.imgContainer}>
        <CaintraLogo className={styles.logo} />
      </div>
      <div className={styles.mainContainer}>
        <div>
          <span className={styles.title}>
            {t("pages.termsAndConditions.title")}
          </span>
        </div>
        <div className={styles.termsWrapper}>
          <div className={styles.textContainer}>
            <h2 className={styles.termHeading}>
              1. {t("pages.termsAndConditions.term1Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term1Text")}
            </p>

            <h2 className={styles.termHeading}>
              2. {t("pages.termsAndConditions.term2Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term2Text")}
            </p>

            <h2 className={styles.termHeading}>
              3. {t("pages.termsAndConditions.term3Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term3Text")}
            </p>

            <h2 className={styles.termHeading}>
              4. {t("pages.termsAndConditions.term4Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term4Text")}
            </p>

            <h2 className={styles.termHeading}>
              5. {t("pages.termsAndConditions.term5Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term5Text")}
            </p>

            <h2 className={styles.termHeading}>
              6. {t("pages.termsAndConditions.term6Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term6Text")}
            </p>

            <h2 className={styles.termHeading}>
              7. {t("pages.termsAndConditions.term7Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term7Text")}
            </p>

            <h2 className={styles.termHeading}>
              8. {t("pages.termsAndConditions.term8Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term8Text")}
            </p>

            <h2 className={styles.termHeading}>
              9. {t("pages.termsAndConditions.term9Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term9Text")}
            </p>

            <h2 className={styles.termHeading}>
              10. {t("pages.termsAndConditions.term10Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term10Text")}
            </p>

            <h2 className={styles.termHeading}>
              11. {t("pages.termsAndConditions.term11Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term11Text")}
            </p>

            <h2 className={styles.termHeading}>
              12. {t("pages.termsAndConditions.term12Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term12Text")}
            </p>

            <h2 className={styles.termHeading}>
              13. {t("pages.termsAndConditions.term13Title")}
            </h2>
            <p className={styles.termParagraph}>
              {t("pages.termsAndConditions.term13Text")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
